import { SelfServiceSchedulePaneProps } from '@tableau/self-service-schedules';
import { DialogType } from '@tableau/widgets';
import * as React from 'react';
import { Provider } from 'react-redux';
import * as _ from 'underscore';
import { incrementRouterSpaceReactKey } from '../../app/actions/spaces/IncrementRouterSpaceReactKeyAction';
import { shareContent } from '../../app/actions/spaces/ShareContentAction';
import { getAppStore } from '../../app/AppStore';
import { DevicePreviewModalBuilder } from '../../devicePreview/utils/DevicePreviewModalBuilder';
import { PublishCompleteDialog, PublishCompleteDialogProps } from '../../app/content/PublishCompleteDialog';
import { Messages } from '../../codegen/Localize';
import { PublishCompleteDialogDisplayedEvent, TelemetryEvents } from '../../common/telemetryEvents/TelemetryEvents';
import { L10n } from '../L10n/L10n';
import { ISyncPublishResult } from '../controllers/DatasourcePublishResultCtrl';
import { FeatureFlags } from '../FeatureFlags';
import { formatListAsString } from '../../schedule/utils/ListToStringFormatter';
import { Filter } from '../helpers/Filter';
import { Order } from '../helpers/Order';
import { IDataSourceBase, IDataSourceDetailInfo } from '../models/IDataSourceInfo';
import { IViewInfo } from '../models/IViewInfo';
import { IWorkbookDetailInfo } from '../models/IWorkbookInfo';
import { DataSources, RefreshOptionType } from '../../content/resources/Datasources';
import { ExtractTasks } from '../../extract/resources/ExtractTasks';
import { RemoteRefreshAgents } from '../resources/RemoteRefreshAgents';
import { Views } from '../../views/resources/Views';
import { Workbooks } from '../../content/resources/Workbooks';
import { IGetRemoteRefreshAgentsResult } from '../ServerApi/requests/GetRemoteRefreshAgentsRequest';
import { FilterFieldNames } from '../ServerApi/types/IFilterClause';
import { RefreshModeTypes } from '../ServerApi/types/IRemoteRefreshProperties';
import { IResult } from '../../httpClient/serverApi/types/IResponse';
import { AngularContextFactory } from '../factories/AngularContextFactory';
import { ChangeDatasourceRefreshModeAction } from './ChangeDatasourceRefreshModeAction';
import {
  ChangeDatasourceRemoteRefreshPropertiesHelper,
  IRemoteRefreshAgentOption,
  IRemoteRefreshPropertiesEditModel,
} from './ChangeDatasourceRemoteRefreshPropertiesHelper';
import { ConfirmActionDialog } from './ConfirmActionDialog';
import { PublishDatasourceAction } from './PublishDatasourceAction';
import { ModalInstance, ReactModalService } from '../../commonInfra/dialogs/ReactModalService';
import { ToasterService, ToastType } from './ToasterService';
import { Telemetry } from '../../commonInfra/services/Telemetry';
import { ServerSettingsUnauthenticated } from '../../settings/services/ServerSettingsUnauthenticated';
import {
  createSelfServiceScheduleExtractAction,
} from '../../app/sagas/spaces/extractTasks/CreateExtractRefreshSaga';
import {IContentInfo} from '../models/IContentInfo';
import {createExtractRefresh} from '../../app/actions/spaces/CreateExtractRefreshAction';
import { SessionSiteSelectors } from '../../app/selectors/SessionInfoSelectors';
import { RefreshSelfServiceScheduleDialog } from '../../app/content/RefreshSelfServiceScheduleDialog';
import { UrlParamKeys } from '~src/app/UrlParamKeys';
import { Helpers } from '~src/ts/helpers/Helpers';
import { asArray, getQueryFromUrl, takeFirstInstanceOf } from '~src/ts/helpers/UrlHelpers';
import { SimpleDeferred } from '@tableau/vizportalclient-common-components';
import { ServiceLocator } from '../../app/ServiceLocator';
import { CreateExtractTaskActionService, CreateExtractTasksMode, IExtractTaskSchedule } from '~src/extract/actions/CreateExtractTaskAction';
import { CreateDatasourceExtractTaskActionService } from '~src/extract/actions/CreateDatasourceExtractTaskAction';
import { CreateWorkbookExtractTaskActionService } from '~src/extract/actions/CreateWorkbookExtractTaskAction';

export enum PostPublishStep {
  Done,
  Schedule,
  LiveQuerySync
}

interface IDatasourceServerTab {
  datasource: IDataSourceBase;
  allowedMode: CreateExtractTasksMode;
  extractTask?: IExtractTaskSchedule;
  resetSchedule: () => void;
  iconClassFn: () => string;
}

interface IScheduleInfo {
  refreshType: string;
}

interface IDatasourceRemoteRefreshWithSSS {
  datasource: IDataSourceBase;
  dataSourceDetail: IDataSourceDetailInfo;
  selfServiceSchedulePaneProps: SelfServiceSchedulePaneProps;
  availableTypeOptions: string[];
  publishResult: ISyncPublishResult;
  remoteAgentOptions: IRemoteRefreshAgentOption[];
  scheduleSummary?: string;
  scheduleInfo?: IScheduleInfo;
  editModel?: IRemoteRefreshPropertiesEditModel;
  resetSchedule: () => void;
  iconClassFn: () => string;
}

interface ISchedulePublishedWorkbookScopeWithSSS extends ng.IScope {
  workbookTab: {
    workbook: IWorkbookDetailInfo;
    allowedMode: CreateExtractTasksMode;
    resetSchedule: () => void;
    extractTask?: IExtractTaskSchedule;
    iconClassFn: () => string;
  };
  datasourceServerRefreshTabs: IDatasourceServerTab[];
  datasourceRemoteRefreshTabs: IDatasourceRemoteRefreshWithSSS[];
}

export namespace PublishWorkbookAction {
  let dialogWithPreviewModalInstance: ModalInstance<unknown>;

  export function handlePublishedWorkbook(workbookDetailInfo: IWorkbookDetailInfo, resultUrl: string) {
    const search = getQueryFromUrl(resultUrl, { arrayFormat: 'comma' });

    const publishResult: ISyncPublishResult = {
      isSyncAvailable: Helpers.BooleanQueryParamRegex.test(takeFirstInstanceOf(search[UrlParamKeys.SyncAvailable])),
      isSyncRunning: Helpers.BooleanQueryParamRegex.test(takeFirstInstanceOf(search[UrlParamKeys.SyncRunning] as string)),
      remoteAgentWorkflowId: takeFirstInstanceOf(search[UrlParamKeys.RemoteAgentWorkflowId]),
      publishedWithLQS: Helpers.BooleanQueryParamRegex.test(takeFirstInstanceOf(search[UrlParamKeys.PublishedWithLQS]))
    };

    const publishedDatasourceUrlNames = search[UrlParamKeys.PublishedDataSources] ?? [];
    const publishedDatasourceLQSUrlNames = search[UrlParamKeys.PublishedDataSourcesWithLQS] ?? [];

    showDialogBasedOnResult(workbookDetailInfo, asArray(publishedDatasourceUrlNames), asArray(publishedDatasourceLQSUrlNames), publishResult);
  }

  function showDialogBasedOnResult(workbook: IWorkbookDetailInfo, publishedDatasourceUrlNames: string[], publishedDatasourcesWithLqsUrlNames: string[], publishResult: ISyncPublishResult) {
    let rootScope = ServiceLocator.get<ng.IRootScopeService>('$rootScope');
    let publishDatasourceAction = ServiceLocator.get<PublishDatasourceAction>('PublishDatasourceAction');

    // Make our best attempt to fire this off, even when the window closes.
    const unloadListener = () =>
      RemoteRefreshAgents.completeWorkflowDuringWindowUnload(publishResult.remoteAgentWorkflowId);
    window.addEventListener('unload', unloadListener, false);

    // Don't attempt to look up remote agent count if datasync isn't enabled on this server.
    const remoteAgentsPromise = ServerSettingsUnauthenticated.isBridgeEnabled() ?
      RemoteRefreshAgents.get() : Promise.resolve({});

    Promise.all([
      getWorkbookExtractTaskCount(workbook),
      DataSources.getPublishedByUrlNames(publishedDatasourceUrlNames),
      DataSources.getPreferredRefreshOptionsByUrlName(publishedDatasourceUrlNames),
      Workbooks.isWorkbookServerRefreshable(workbook.id),
      remoteAgentsPromise
    ]).then((results: any[]): Promise<any> => {
        const workbookExtractTaskCount = results[0];
        const datasources = results[1];
        const preferredRefreshOptions = results[2];
        const isWorkbookServerRefreshable = results[3] && results[3].refreshable;
        const hasRemoteAgents =  ServerSettingsUnauthenticated.isBridgeEnabled() && !_.isEmpty(results[4].agents);

        const shouldRefreshWorkbook = (workbookExtractTaskCount === 0 && isWorkbookServerRefreshable);
        const serverRefreshDatasources: IDataSourceBase[] = [];
        const remoteRefreshDatasources: IDataSourceBase[] = [];
        const remoteLiveSyncDatasources: IDataSourceBase[] = [];

        _.forEach(datasources.items, (datasource: IDataSourceBase) => {
          const refreshOptions = preferredRefreshOptions[datasource.urlName];
          if (refreshOptions && refreshOptions.suggestion) {
            switch (refreshOptions.suggestion) {
              case RefreshOptionType.Server:
                serverRefreshDatasources.push(datasource);
                break;
              case RefreshOptionType.OnlineSync:
                if (hasRemoteAgents || publishDatasourceAction.canReserveSyncWithWorkflow(publishResult)) {
                  remoteRefreshDatasources.push(datasource);
                }
                break;
              case RefreshOptionType.LiveConnection:
                if(publishedDatasourcesWithLqsUrlNames.indexOf(datasource.urlName) !== -1 && hasRemoteAgents) {
                  remoteLiveSyncDatasources.push(datasource);
                }
                break;
            }
          }
        });

        // There are six different cases here:
        // Case 1: There's nothing to schedule, so show a "Done" button that closes the dialog. (This includes "remote refreshes required, but no agents are available." and "The workbook already has a schedule.")
        // Case 2: Only the workbook needs a schedule, so show the workbook extract refresh schedule dialog.
        // Case 3: Only a single datasource needs a schedule, and it's a server refresh, so show the datasource extract server-refresh schedule dialog.
        // Case 4: Only a single datasource needs a schedule, and it's a remote refresh, so show the datasource extract remote-refresh schedule dialog.
        // Case 5: There are just live query sync datasources, so show the corresponding text
        // Case 6: More than one thing needs a schedule or an agent, so show the tabbed schedule interface with those things.
        if (!shouldRefreshWorkbook && serverRefreshDatasources.length === 0 && remoteRefreshDatasources.length === 0 && remoteLiveSyncDatasources.length === 0) {
          // Case 1: There's nothing to schedule, so show a "Done" button that closes the dialog.
          // (This includes "remote refreshes required, but no agents are available." and "The workbook already has a schedule.")

          return displaySuccessDialog(workbook, datasources.items, PostPublishStep.Done);

        } else if (shouldRefreshWorkbook && serverRefreshDatasources.length === 0 && remoteRefreshDatasources.length === 0 && remoteLiveSyncDatasources.length === 0) {
          // Case 2: Only the workbook needs a schedule, so show the workbook extract refresh schedule dialog.

          return displaySuccessDialog(workbook, datasources.items, PostPublishStep.Schedule)
            .then(() => CreateWorkbookExtractTaskActionService.forWorkbook(workbook, {
              scope: rootScope,
              targetElement: null
            }, CreateExtractTasksMode.AllowScheduleOnServer))
            .then(() => getAppStore().dispatch(incrementRouterSpaceReactKey()));

        } else if (!shouldRefreshWorkbook && serverRefreshDatasources.length === 1 && remoteRefreshDatasources.length === 0 && remoteLiveSyncDatasources.length === 0) {
          // Case 3: Only a single datasource needs a schedule, and it's a server refresh, so show the datasource extract server-refresh schedule dialog.

          const datasource = serverRefreshDatasources[0];
          return displaySuccessDialog(workbook, datasources.items, PostPublishStep.Schedule)
            .then(() => changeDSRefreshMode(datasource, RefreshModeTypes.Server))
            .then(() => CreateDatasourceExtractTaskActionService.forDatasource(datasource, {
              scope: rootScope,
              targetElement: null
            }, CreateExtractTasksMode.AllowScheduleOnServer))
            .then(() => getAppStore().dispatch(incrementRouterSpaceReactKey()));

        } else if (!shouldRefreshWorkbook && serverRefreshDatasources.length === 0 && remoteRefreshDatasources.length === 1 && remoteLiveSyncDatasources.length === 0) {
          // Case 4: Only a single datasource needs a schedule, and it's a remote refresh, so show the datasource extract remote-refresh schedule dialog.

          const datasource = remoteRefreshDatasources[0];

          if (FeatureFlags.selfServiceSchedulesForRefreshEnabled && SessionSiteSelectors.isSelfServiceSchedulesForRefreshEnabled(getAppStore().getState())) {
            if (datasource.isFileBased) {
              return displaySuccessDialog(workbook, datasources.items, PostPublishStep.Schedule)
                .then(() => ChangeDatasourceRefreshModeAction.execute(datasource, RefreshModeTypes.Remote, null, true, false))
                .then(() => CreateDatasourceExtractTaskActionService.forDatasource(datasource, {
                  scope: rootScope,
                  targetElement: null
                },  CreateExtractTasksMode.AllowScheduleOnlyOnClient)
                  .then(() => getAppStore().dispatch(incrementRouterSpaceReactKey())));
            } else{
              const contentInfo = [datasource] as IContentInfo[];
              const action = createExtractRefresh(contentInfo, CreateExtractTasksMode.AllowScheduleOnServer);
              return displaySuccessDialog(workbook, datasources.items, PostPublishStep.Schedule)
                .then(() => ChangeDatasourceRefreshModeAction.execute(datasource, RefreshModeTypes.Server, null, true, false))
                .then(() => createSelfServiceScheduleExtractAction(action))
                .then(() => getAppStore().dispatch(incrementRouterSpaceReactKey()));
            }
          } else {
            return displaySuccessDialog(workbook, datasources.items, PostPublishStep.Schedule)
              .then(() => changeDSRefreshMode(datasource, RefreshModeTypes.Server))
              .then(() => CreateDatasourceExtractTaskActionService.forDatasource(datasource, {
                scope: rootScope,
                targetElement: null
              }, CreateExtractTasksMode.AllowScheduleOnServerAndClient))
              .then(() => getAppStore().dispatch(incrementRouterSpaceReactKey()));
          }

        } else if (!shouldRefreshWorkbook && serverRefreshDatasources.length === 0 && remoteRefreshDatasources.length === 0 && remoteLiveSyncDatasources.length >= 1) {
          // Case 5: There are just live query sync datasources so show the corresponding messages

          return displaySuccessDialog(workbook, datasources.items, PostPublishStep.LiveQuerySync);

        } else {
          // Case 6: More than one thing needs a schedule, so show the tabbed schedule interface with those things.

          return RemoteRefreshAgents.get().then((remoteAgents: IGetRemoteRefreshAgentsResult) => {
            const remoteAgentOptions = buildRemoteAgentOptions(publishResult, remoteAgents, publishDatasourceAction);
            return displaySuccessDialog(workbook, datasources.items, PostPublishStep.Schedule)
              .then(() => {
                const isExtractSSSEnabledForServer = FeatureFlags.selfServiceSchedulesForRefreshEnabled;
                const isExtractSSSEnabledForSite = SessionSiteSelectors.isSelfServiceSchedulesForRefreshEnabled(getAppStore().getState());

                // We do not want to show admin schedules option if SSS is enabled
                // A common schedules dialog will be shown for both server and remote refreshes in this multi datasource case
                if (isExtractSSSEnabledForServer && isExtractSSSEnabledForSite) {
                  const allDataSources = serverRefreshDatasources.concat(remoteRefreshDatasources);
                  let deferredPromise = new SimpleDeferred();
                  RefreshSelfServiceScheduleDialog.open(CreateExtractTaskActionService.getRefreshSSSDialogParams(allDataSources)).then(result => deferredPromise.resolve(result))
                    .catch(error => deferredPromise.reject(error));
                  return deferredPromise.promise;
                }

                return displayTabbedScheduleDialogWithSSS(serverRefreshDatasources, remoteRefreshDatasources, publishResult, remoteAgentOptions, shouldRefreshWorkbook, rootScope, publishDatasourceAction, workbook);
              });
          });
        }
      })
      .finally(() => {
        window.removeEventListener('unload', unloadListener, false);
        RemoteRefreshAgents.completeWorkflow(publishResult.remoteAgentWorkflowId);
      });
  }

  function buildRemoteAgentOptions(publishResult: ISyncPublishResult, remoteAgents: IGetRemoteRefreshAgentsResult, publishDatasourceAction: PublishDatasourceAction): IRemoteRefreshAgentOption[] {
    if (publishDatasourceAction.canReserveSyncWithWorkflow(publishResult) && _.isEmpty(remoteAgents)) {
      // Add "My Computer" option if there are no agents
      return [publishDatasourceAction.getNewAgentReservationOption()];
    } else {
      // Use existing remote agents
      return ChangeDatasourceRemoteRefreshPropertiesHelper.buildRemoteAgentsList(remoteAgents);
    }
  }

  function displaySuccessDialog(workbook: IWorkbookDetailInfo, datasources: IDataSourceBase[], postPublishStep: PostPublishStep): Promise<any> {
    const orderClause = [ Order.asc('index') ];
    const viewsQuery = Views.forWorkbook(workbook).withOrder(orderClause);

    return viewsQuery.getCount()
    .then((viewCount) => viewsQuery.getSlice(0, viewCount))
    .then((result) => result.items)
    .then((viewList: IViewInfo[]) =>{
      const dialogProps = getPublishCompleteDialogProps(viewList, workbook, datasources, postPublishStep);
      Telemetry.sendEvent<PublishCompleteDialogDisplayedEvent>({
        type: TelemetryEvents.PublishCompleteDialogDisplayed,
        isPreviewDeviceLayoutsLinkDisplayed: dialogProps.shouldDisplayDevicePreview
      });

      return buildPublishSuccessDialogFromProps(dialogProps).result;
    }) as Promise<any>;
  }

  export function buildPublishSuccessDialogFromProps(props: PublishCompleteDialogProps): ModalInstance<unknown> {
    dialogWithPreviewModalInstance = ReactModalService.open({
      type: DialogType.NormalWithTitleBar,
      title: PublishCompleteDialog.Title(),
      dialogContent: React.createElement(PublishCompleteDialog.Content, props),
      footerContent: React.createElement(Provider, {store: getAppStore()},
        React.createElement(PublishCompleteDialog.ConnectedFooter, props)
      ),
      testId: 'publish-workbook-success-modal',
      dialogWidth: 550,
      showCloseButton: true,
      dismissDialogOnCloseAction: true // Must dismiss here to prevent the schedule dialog from opening
    });

    return dialogWithPreviewModalInstance;
  }

  export function getPublishCompleteDialogProps(
    viewList: IViewInfo[],
    workbook: IWorkbookDetailInfo,
    datasources: IDataSourceBase[],
    postPublishStep: PostPublishStep): PublishCompleteDialogProps
  {
    let publishLiveQueryInfo = '';
    if (postPublishStep === PostPublishStep.LiveQuerySync) {
      publishLiveQueryInfo = getLiveQueryInfoText(datasources);
    }

    return {
      // We need to use a member variable here because the caller of this method will mutate this modal instance
      // after the dialog is displayed. We need this mutation to propagate to these functions, so we store the
      // instance where we can retrieve it later.
      dismiss: () => dialogWithPreviewModalInstance && dialogWithPreviewModalInstance.dismiss(),
      close: () => dialogWithPreviewModalInstance && dialogWithPreviewModalInstance.close(0),
      onDevicePreviewClick: () => openDevicePreviewForViz(viewList[0].path, dialogWithPreviewModalInstance),
      shareContent: shareContent,
      workbook: workbook,
      datasources: datasources,
      getDatasourceIconClass: getDatasourceIconClass,
      publishLiveQueryInfo: publishLiveQueryInfo,
      shouldDisplayExtensionServiceSection: FeatureFlags.analyticsExtensionsNamedConnections,
      shouldDisplayDevicePreview: viewList.length > 0,
      shouldDisplayLiveQueryInfo: postPublishStep === PostPublishStep.LiveQuerySync,
      shouldDisplayScheduleButton: postPublishStep === PostPublishStep.Schedule,
    };
  }

  function openDevicePreviewForViz(vizPath: string, publishCompleteModalInstance: ModalInstance<unknown>) {
    let rootScope = ServiceLocator.get<ng.IRootScopeService>('$rootScope');
    let angularContext = ServiceLocator.get<AngularContextFactory>('AngularContextFactory').create(rootScope.$new());

    Telemetry.sendEvent({
      type: TelemetryEvents.PreviewDeviceLayoutsLinkClicked
    });

    DevicePreviewModalBuilder.openPreview(angularContext, vizPath);
    publishCompleteModalInstance.dismiss();
  }

  function getLiveQueryInfoText(datasources: IDataSourceBase[]) {
    const datasourceNames = datasources.map(function(datasource){return datasource.name;}).join(',');
    return Messages.publishWorkbookSuccessLiveQueryInfo({'ITEM_NAME': L10n.elideIfLong(datasourceNames, 50)});
  }

  function displayTabbedScheduleDialogWithSSS(serverRefreshDatasources: IDataSourceBase[], remoteRefreshDatasources: IDataSourceBase[], publishResult: ISyncPublishResult, agents: IRemoteRefreshAgentOption[], shouldRefreshWorkbook: boolean, rootScope: ng.IRootScopeService, publishDatasourceAction: PublishDatasourceAction, workbook?: IWorkbookDetailInfo,) {
    const dialogScope = rootScope.$new() as ISchedulePublishedWorkbookScopeWithSSS;
    let confirmActionDialog = ServiceLocator.get<ConfirmActionDialog>('ConfirmActionDialog');

    // Build a tab for the workbook, if there is one
    if (workbook && shouldRefreshWorkbook) {
      dialogScope.workbookTab = {
        workbook: workbook,
        allowedMode: CreateExtractTasksMode.AllowScheduleOnServer,
        resetSchedule: () => { dialogScope.workbookTab.extractTask.schedule = null; },
        iconClassFn: () => 'tb-data-pane-workbook-icon'
      };
    }

    // Build a tab for every server-refresh datasource, if there are any
    dialogScope.datasourceServerRefreshTabs = [];
    if (serverRefreshDatasources) {
      _.forEach(serverRefreshDatasources, (datasource: IDataSourceBase) => {
          const datasourceTab: IDatasourceServerTab = {
            datasource: datasource,
            allowedMode: CreateExtractTasksMode.AllowScheduleOnServer,
            resetSchedule: () => { datasourceTab.extractTask.schedule = null; },
            iconClassFn: () => getDatasourceIconClass(datasource)
          };
          dialogScope.datasourceServerRefreshTabs.push(datasourceTab);
      });
    }

    // Build a tab for every remote-refresh datasource, if there are any
    dialogScope.datasourceRemoteRefreshTabs = [];
    if (remoteRefreshDatasources) {
      _.forEach(remoteRefreshDatasources, (datasource: IDataSourceBase) => {
        const datasourceTab: IDatasourceServerTab = {
          datasource: datasource,
          allowedMode: CreateExtractTasksMode.AllowScheduleOnServer,
          resetSchedule: () => { datasourceTab.extractTask.schedule = null; },
          iconClassFn: () => getDatasourceIconClass(datasource)
        };
        dialogScope.datasourceServerRefreshTabs.push(datasourceTab);
      });
    }

    const dialogInstance = confirmActionDialog.open({
      scope: dialogScope,
      position: {reserveHeight: 550},
      bodyTemplateUrl: 'publishWorkbookTabsWithSSS.html',
      title: Messages.publishWorkbookScheduleDialogTitle(),
      size: '730px',
      actionName: Messages.publishWorkbookScheduleDialogButton_schedule(),
      action: () => {
        const promises: Promise<IResult>[] = [];
        const itemNames: string[] = [];

        // Create workbook extract task
        if (dialogScope.workbookTab && dialogScope.workbookTab.extractTask && dialogScope.workbookTab.extractTask.schedule) {
          promises.push(Workbooks.createExtractTask([workbook.id], dialogScope.workbookTab.extractTask.schedule.id, dialogScope.workbookTab.extractTask.type));
          itemNames.push(workbook.name);
        }

        // Create datasource server refresh extract tasks
        _.forEach(dialogScope.datasourceServerRefreshTabs, (datasourceTab: IDatasourceServerTab) => {
          if (datasourceTab.extractTask && datasourceTab.extractTask.schedule) {
            promises.push(DataSources.createExtractTask([datasourceTab.datasource.id], datasourceTab.extractTask.schedule.id, datasourceTab.extractTask.type));
            itemNames.push(datasourceTab.datasource.name);
          }
        });

        // Create datasource remote refresh extract tasks
        _.forEach(dialogScope.datasourceRemoteRefreshTabs, (datasourceTab: IDatasourceRemoteRefreshWithSSS) => {
          promises.push(publishDatasourceAction.createRemoteRefreshSelfServiceSchedule(datasourceTab.datasource, datasourceTab.scheduleInfo.refreshType,
            datasourceTab.selfServiceSchedulePaneProps.recurrenceModel, datasourceTab.editModel, publishResult, /* suppressToast */ true) as Promise<IResult>);
          itemNames.push(datasourceTab.datasource.name);
        });

        Promise.all(promises).then(() => {
          if (itemNames.length > 0) {
            const itemNamesStr = formatListAsString(itemNames);
            ToasterService.instance.pop({ type: ToastType.Success, text: Messages.publishSchedulesReady({ ITEM_NAMES: itemNamesStr, ITEM_COUNT: itemNames.length })});
          }
        });

      }
    });

    dialogInstance.result.finally(() => dialogScope.$destroy());

    return dialogInstance.result;
  }

  function getWorkbookExtractTaskCount(workbook: IWorkbookDetailInfo): Promise<number> {
    let query = ExtractTasks.all();
    query = query.addFilter(Filter.fieldEq(FilterFieldNames.workbookId, workbook.id));
    return query.getCount() as Promise<number>;
  }

  function changeDSRefreshMode(datasource: IDataSourceBase, refreshMode: string) {
    // Changing refresh modes causes an error on on-prem servers, so only do it if datasync is an option.
    if (ServerSettingsUnauthenticated.isBridgeEnabled()) {
      return ChangeDatasourceRefreshModeAction.execute(datasource, refreshMode, null, true, false);
    } else {
      return Promise.resolve({});
    }
  }

  function getDatasourceIconClass(datasource: IDataSourceBase): string {
    if (!datasource.hasExtracts && !datasource.isHierarchical) {
      return 'tb-data-pane-datasource-icon';
    } else if (datasource.hasExtracts && !datasource.isHierarchical) {
      return 'tb-data-pane-datasource-extract-icon';
    } else {
      return 'tb-data-pane-datasource-cube-icon';
    }
  }
}

export type PublishWorkbookAction = typeof PublishWorkbookAction;
